import { useRef, useState } from "react"
import { MapPin, ArrowRight, Shield } from "lucide-react"
import { useWizard } from "./WizardContext"
import { useAutocomplete, getSatelliteImageUrl } from "@/hooks/useGoogleMaps"
import { Button } from "@/components/ui/button"

export default function AddressStep() {
  const { setAddress, setRoofImage, nextStep } = useWizard()
  const inputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<{ url: string; address: string } | null>(null)

  useAutocomplete(inputRef, (place) => {
    setAddress(place.address, place.formattedAddress, place.lat, place.lng)
    const imageUrl = getSatelliteImageUrl(place.lat, place.lng)
    setRoofImage(imageUrl)
    setPreview({ url: imageUrl, address: place.formattedAddress })
  })

  const handleContinue = () => {
    if (preview) nextStep()
  }

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div>
        <h2 className="text-2xl font-bold text-foreground tracking-tight">
          Where is the property?
        </h2>
        <p className="text-muted-foreground mt-1">
          Enter your address and we'll pull up a satellite view of your roof.
        </p>
      </div>

      {/* Address input */}
      <div className="relative group">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors">
          <MapPin className="w-5 h-5" />
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder="123 Main St, Dayton OH 45402"
          className="w-full pl-12 pr-4 py-4 bg-white border-2 border-muted rounded-xl text-[15px] font-medium text-foreground outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all placeholder:text-muted-foreground/60"
          autoFocus
        />
      </div>

      {/* Satellite preview */}
      {preview && (
        <div className="space-y-4 animate-fade-up">
          <div className="relative rounded-xl overflow-hidden border-2 border-muted shadow-sm">
            <img
              src={preview.url}
              alt="Satellite view of property"
              className="w-full aspect-square object-cover"
              crossOrigin="anonymous"
            />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-4">
              <p className="text-white text-sm font-medium">{preview.address}</p>
            </div>
          </div>

          <Button
            onClick={handleContinue}
            className="w-full h-13 bg-primary hover:bg-primary-dark text-white text-base font-bold rounded-xl transition-all hover:shadow-lg hover:shadow-primary/25"
          >
            That's my roof
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      )}

      {/* Trust signal */}
      <div className="flex items-center gap-2 text-muted-foreground text-xs pt-2">
        <Shield className="w-3.5 h-3.5" />
        <span>Your information is secure and never shared with third parties.</span>
      </div>
    </div>
  )
}
